import { useState, useMemo } from 'react';
import { startOfDay, endOfDay } from 'date-fns';
import { SortableDeliveryList } from './SortableDeliveryList';
import { AddDeliveryToRouteDialog } from './AddDeliveryToRouteDialog';
import { useDeliveries } from '@/hooks/useDeliveries';
import { useDeliveryMutations } from '@/hooks/useDeliveryMutations';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, CheckCircle, Plus, MapPin, Clock, Store, Truck as TruckIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { DeliveryWithDetails } from '@/types/delivery';

export const DriverRouteView = () => {
  const { user } = useAuth();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { updateDeliveryStatus, isUpdating } = useDeliveryMutations();

  // Filtros server-side: apenas entregas do motorista, apenas hoje
  const todayRange = useMemo(() => ({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
    assignedTo: user?.id,
  }), [user?.id]);

  const { deliveries, isLoading, refetch } = useDeliveries(todayRange);

  // Dados já filtrados server-side por assignedTo
  const myDeliveries = deliveries ?? [];
  
  // Separar por status e método
  const pendingDeliveries = myDeliveries
    .filter(d => d.status !== 'delivered' && d.status !== 'cancelled' && d.status !== 'failed' && (!d.delivery_method || d.delivery_method === 'delivery'))
    .sort((a, b) => (a.route_order || 0) - (b.route_order || 0));
  
  // Coletas (retirada na loja ou transportadora)
  const pickupDeliveries = myDeliveries
    .filter(d => d.status !== 'delivered' && d.status !== 'cancelled' && d.status !== 'failed' && (d.delivery_method === 'pickup' || d.delivery_method === 'shipping'))
    .sort((a, b) => (a.route_order || 0) - (b.route_order || 0));
  
  const today = new Date().toISOString().split('T')[0];
  const completedTodayDeliveries = myDeliveries.filter(
    d => d.status === 'delivered' && d.delivered_at?.split('T')[0] === today
  );

  const currentDeliveryIds = myDeliveries.map(d => d.id);

  

  const handleReorder = (newOrder: DeliveryWithDetails[]) => {
    // A lista já atualiza internamente
  };

  const handleDeliveryCompleted = () => {
    refetch();
  };

  // Handle pickup completion - actually updates delivery status
  const handlePickupCompleted = async (deliveryId: string) => {
    try {
      await updateDeliveryStatus({
        deliveryId,
        status: 'delivered',
      });
      refetch();
    } catch (error) {
      console.error('Erro ao marcar coleta:', error);
    }
  };

  if (isLoading && !deliveries) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <Card>
              <CardContent className="p-4">
                <div className="h-24 bg-muted rounded" />
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="gap-2">
            <TruckIcon className="h-4 w-4" />
            Entregas ({pendingDeliveries.length})
          </TabsTrigger>
          <TabsTrigger value="pickup" className="gap-2">
            <Store className="h-4 w-4" />
            Coletas ({pickupDeliveries.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Concluídas ({completedTodayDeliveries.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          <SortableDeliveryList
            deliveries={pendingDeliveries}
            onReorder={handleReorder}
            onAddDelivery={() => setShowAddDialog(true)}
            onDeliveryCompleted={handleDeliveryCompleted}
          />
        </TabsContent>

        <TabsContent value="pickup" className="space-y-4">
          {pickupDeliveries.length === 0 ? (
            <div className="text-center py-12">
              <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
                <Store className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-lg">Nenhuma coleta pendente</h3>
              <p className="text-sm text-muted-foreground">
                Retiradas na loja e envios por transportadora aparecerão aqui
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pickupDeliveries.map((delivery) => (
                <Card key={delivery.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        {delivery.delivery_method === 'pickup' ? (
                          <Store className="h-5 w-5 text-purple-600" />
                        ) : (
                          <Package className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="font-medium">
                              {delivery.sale?.customer?.name || delivery.contact_name || 'Cliente'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {delivery.delivery_number}
                            </p>
                          </div>
                          <Badge className={delivery.delivery_method === 'pickup' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'}>
                            {delivery.delivery_method === 'pickup' ? 'Retirada' : delivery.shipping_carrier || 'Transportadora'}
                          </Badge>
                        </div>
                        {delivery.contact_phone && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>Tel: {delivery.contact_phone}</span>
                          </div>
                        )}
                        {delivery.items && delivery.items.length > 0 && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {delivery.items.length} item(ns)
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t">
                      <Button
                        className="flex-1 h-10 gap-2 bg-green-600 hover:bg-green-700"
                        onClick={() => handlePickupCompleted(delivery.id)}
                        disabled={isUpdating}
                      >
                        <CheckCircle className="h-4 w-4" />
                        {isUpdating ? 'Processando...' : 'Marcar como Coletado'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedTodayDeliveries.length === 0 ? (
            <div className="text-center py-12">
              <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-lg">Nenhuma entrega concluída hoje</h3>
              <p className="text-sm text-muted-foreground">
                As entregas finalizadas aparecerão aqui
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedTodayDeliveries.map((delivery) => (
                <Card key={delivery.id} className="border-green-200 bg-green-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="font-medium">
                              {delivery.sale?.customer?.name || delivery.contact_name || 'Cliente'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {delivery.delivery_number}
                            </p>
                          </div>
                          <Badge className="bg-green-500 text-white">Entregue</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {delivery.city || 'Endereço não informado'}
                          </span>
                          {delivery.delivered_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(delivery.delivered_at), 'HH:mm', { locale: ptBR })}
                            </span>
                          )}
                        </div>
                        {delivery.signature_customer_name && (
                          <p className="text-sm mt-2 text-green-700">
                            Recebido por: {delivery.signature_customer_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddDeliveryToRouteDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        currentDeliveryIds={currentDeliveryIds}
        onSuccess={refetch}
      />
    </div>
  );
};
